import { isPlatformBrowser } from '@angular/common';
import { Inject, Injectable, PLATFORM_ID } from '@angular/core';
import { BehaviorSubject } from 'rxjs';

@Injectable({ providedIn: 'root' })
export class ThemeService {

  private storageKey = 'theme';
  private isBrowser: boolean;
  private darkModeSubject = new BehaviorSubject<boolean>(false);
  darkMode$ = this.darkModeSubject.asObservable();

  constructor(@Inject(PLATFORM_ID) private platformId: Object) {
    this.isBrowser = isPlatformBrowser(this.platformId);
  }

  setTheme(theme: 'light' | 'dark') {
    if (!this.isBrowser) return;
    document.documentElement.setAttribute('data-theme', theme);
    localStorage.setItem(this.storageKey, theme);
    this.darkModeSubject.next(theme === 'dark');
  }

  toggleTheme() {
    const current = this.getTheme();
    const next = current === 'dark' ? 'light' : 'dark';
    this.setTheme(next);
  }

  getTheme(): 'light' | 'dark' {
    if (!this.isBrowser) return 'light';
    return (localStorage.getItem(this.storageKey) as 'light' | 'dark') || 'light';
  }

  initTheme() {
    if (!this.isBrowser) return;
    const savedTheme = localStorage.getItem(this.storageKey);

    if (savedTheme) {
      this.setTheme(savedTheme as 'light' | 'dark');
    } else {
      const prefersDark = window.matchMedia('(prefers-color-scheme: dark)').matches;
      this.setTheme(prefersDark ? 'dark' : 'light');
    }
  }

  isDarkMode(): boolean {
    return this.getTheme() === 'dark';
  }

  enableDarkMode() {
    this.setTheme('dark');
  }

  disableDarkMode() {
    this.setTheme('light');
  }
}